import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, Loading } from '@/components/ui/daisy';
import useLessonMaterial from '@/data/hook/book/useLessonMaterial';
import { bookType, MaterialType } from '@/models/Book';

import MaterialCard from './MaterialCard';
import PdfMaterialFormService from './PdfMaterialFormService';

type MaterialSectionProps = {
  lessonId: number;
  bookType: bookType;
  bookId: number;
};

export default function MaterialSection({
  lessonId,
  bookType,
  bookId,
}: MaterialSectionProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.materialsTab.singleMaterialWarning',
  });

  const { book, isLoading, deleteMaterial, editMaterial, isEditing } =
    useLessonMaterial({ lessonId, bookType, bookId });

  const [isPdfEditing, setPdfEditing] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loading variant="spinner" size="lg" color="primary" />
      </div>
    );
  }

  if (!book) return null;

  const isPdf = book.materialType === MaterialType.PDF_INTERACTIVE;
  const pdfFile = isPdf ? book.file : null;

  if (isPdfEditing)
    return (
      <PdfMaterialFormService
        lessonId={lessonId}
        bookType={bookType}
        book={book}
        onClose={() => setPdfEditing(false)}
      />
    );

  return (
    <div className="flex flex-col gap-6">
      <MaterialCard
        materialType={book.materialType}
        description={isPdf ? book.filename : undefined}
        onDelete={deleteMaterial}
        onEdit={isPdf ? () => setPdfEditing(true) : editMaterial}
        onPreview={
          pdfFile
            ? () => window.open(pdfFile, '_blank', 'noopener,noreferrer')
            : undefined
        }
        isEditing={isEditing}
      />
      <Alert variant="soft" color="info" className="rounded-2xl">
        <InformationCircleIcon className="size-6 shrink-0" />
        <div className="flex flex-col gap-1">
          <span className="font-semibold">{t('title')}</span>
          <span className="text-sm">{t('description')}</span>
        </div>
      </Alert>
    </div>
  );
}
